// localStorage 封裝 — 玩家設定持久化
// 對應 PRD §3.2.6 Storage、§13.6 localStorage 結構
//
// MVP 只存：
// - audioOn：音效開關
// - bgmOn：背景音樂開關
// - discardTimeout：出牌秒數（8 / 15 / 30 / null=不催促）
//
// 設計原則：
// - 純函式 + 預設值，呼叫端不需處理「key 不存在」邊界
// - JSON parse 失敗時回 default（容錯，避免 bad data 卡住應用程式）
// - 鍵名加 prefix 避免與其他應用衝突

/** 玩家設定型別。 */
export type TSettings = {
  /** 音效開關（PRD §11.3 預設 ON） */
  readonly audioOn: boolean;
  /** 背景音樂開關（PRD §11.3 預設 OFF） */
  readonly bgmOn: boolean;
  /** 出牌秒數（null = 不催促） */
  readonly discardTimeout: 8 | 15 | 30 | null;
  /** 點兩下才出牌（第一下升起放大、第二下打出；PRD-Freemium §9 防誤觸，預設 ON） */
  readonly doubleTapDiscard: boolean;
};

/** PRD §13.6 規定的 localStorage key。 */
const STORAGE_KEY = 'mahjongking.settings';

/** discardTimeout 合法值（null = 不催促）。 */
const isValidTimeout = (v: unknown): v is TSettings['discardTimeout'] =>
  v === null || v === 8 || v === 15 || v === 30;

/** 預設設定（PRD §11.3）。 */
export const DEFAULT_SETTINGS: TSettings = {
  audioOn: true,
  bgmOn: false,
  discardTimeout: null,
  doubleTapDiscard: true,
};

/**
 * 讀取設定。
 * - 沒存過 → 回預設
 * - 解析失敗 → 回預設（防 bad data）
 * - 部分欄位缺失 → 用預設補齊
 */
export const loadSettings = (): TSettings => {
  if (typeof localStorage === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<TSettings>;
    return {
      audioOn: typeof parsed.audioOn === 'boolean' ? parsed.audioOn : DEFAULT_SETTINGS.audioOn,
      bgmOn: typeof parsed.bgmOn === 'boolean' ? parsed.bgmOn : DEFAULT_SETTINGS.bgmOn,
      // 值域校驗：竄改 / 舊版寫入的非法值（如 99、字串）一律落回預設，
      // 守住型別宣告的 8 | 15 | 30 | null 保證，不讓壞資料原樣外流到倒數邏輯。
      discardTimeout: isValidTimeout(parsed.discardTimeout)
        ? parsed.discardTimeout
        : DEFAULT_SETTINGS.discardTimeout,
      doubleTapDiscard:
        typeof parsed.doubleTapDiscard === 'boolean'
          ? parsed.doubleTapDiscard
          : DEFAULT_SETTINGS.doubleTapDiscard,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

/**
 * 寫入設定（整個物件覆寫）。
 * 寫入失敗（quota 超限、隱私模式等）時靜默忽略。
 */
export const saveSettings = (settings: TSettings): void => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // 寫入失敗不影響遊戲運作；下次讀取時還是回預設
  }
};

/** 部分更新設定（merge 後寫入）。 */
export const updateSettings = (partial: Partial<TSettings>): TSettings => {
  const current = loadSettings();
  const next: TSettings = { ...current, ...partial };
  saveSettings(next);
  return next;
};
